/**
 * Role-scoped route access for the ADI frontend.
 *
 * Most routes declare capabilities via router meta (`requiresAdmin`,
 * `requiresPlatformAdmin`, etc.). COMPLIANCE_OPS uses a workspace allowlist so
 * new compliance routes under `/compliance/*` do not silently dead-end.
 *
 * @see docs/adr/0020-canonical-entity-compliance-rollup.md
 */

/** Session storage key — App.vue reads and shows a toast, then clears. */
export const ACCESS_DENIED_STORAGE_KEY = 'adi_route_access_denied'

const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/logout',
  '/forgot-password',
  '/password-reset',
  '/secure-upload',
  '/unsubscribe',
  '/portal',
  '/sign/',
]

/**
 * Paths COMPLIANCE_OPS may navigate to (prefix match).
 * Keep in sync with NavSidebar + ComplianceSpokeNav deep links.
 * `/coi` remains allowlisted so the legacy redirect to `/clients` works.
 */
export const COMPLIANCE_OPS_WORKSPACE_PREFIXES = [
  '/coi',
  '/coi-import',
  '/compliance',
  '/compliance-health',
  '/compliance-report',
  '/compliance-responsiveness',
  '/clients',
  '/projects',
  '/agent-activity',
  '/client-management',
  '/growth-contacts',
  '/workflow-from-document',
  '/prompt-enhancement',
  '/check-definition-editor',
  '/intake-queue',
  '/active-locations-report',
  '/carrier-intelligence',
  '/carrier-analytics',
  '/setup/compliance-client',
  '/batch-processing',
  '/status-changes',
  '/profile',
  '/settings',
  '/settings/profile',
  '/settings/security',
  '/settings/billing',
  '/settings/marketing',
]

const SPEAKER_ALLOWED_PREFIXES = [
  '/learning',
  '/vet-consult',
  '/profile',
  '/settings/profile',
  '/logout',
  '/login',
  '/forgot-password',
  '/password-reset',
  '/speakers',
  '/sign/developer-agreement',
]

function pathMatchesPrefix(path, prefix) {
  return path === prefix || path.startsWith(`${prefix}/`)
}

export function isPublicPath(path) {
  return PUBLIC_PATH_PREFIXES.some((p) => pathMatchesPrefix(path, p))
}

export function isSpeakerAllowedPath(path) {
  return SPEAKER_ALLOWED_PREFIXES.some((p) => pathMatchesPrefix(path, p))
}

export function isComplianceOpsAllowedPath(path) {
  if (path === '/') {
    return true
  }
  return COMPLIANCE_OPS_WORKSPACE_PREFIXES.some((p) => pathMatchesPrefix(path, p))
}

/**
 * @param {string} path
 * @returns {string}
 */
export function getComplianceOpsRedirectMessage(path) {
  return `That page (${path}) is outside your Compliance Operations workspace. Try Home (Portfolio) or Compliance Health.`
}

/**
 * @param {string} attemptedPath
 */
export function storeAccessDeniedNotice(attemptedPath) {
  try {
    sessionStorage.setItem(
      ACCESS_DENIED_STORAGE_KEY,
      JSON.stringify({
        attempted: attemptedPath,
        message: getComplianceOpsRedirectMessage(attemptedPath),
      }),
    )
  } catch {
    // sessionStorage unavailable — redirect still applies
  }
}
